import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Modal, Empty, Spinner, PasswordInput, OtpInputs, useToast } from "../../components/ui";
import { initials, fmtDateTime } from "../../helpers";

const ROLE_OPTS = [["USER", "User"], ["SUPERVISOR", "Supervisor"], ["ADMIN", "Admin"], ["SUPER_ADMIN", "Super admin"], ["CLIENT_ADMIN", "Client admin"], ["AUDITOR", "Auditor"]];
const UM_ROLE = { USER: "User", SUPERVISOR: "Supervisor", ADMIN: "Admin", SUPER_ADMIN: "Super admin", CLIENT_ADMIN: "Client admin", AUDITOR: "Auditor" };
const UM_STATUS = { ACTIVE: "Active", SUSPENDED: "Suspended", PENDING: "Pending", DISABLED: "Disabled" };
const PROC_OPTS = [["SUBMITTER", "Submitter"], ["UPLOADER", "Uploader"], ["MODIFIER", "Modifier"], ["MORE_INFO", "More Info"]];
const SHIFT_OPTS = [["", "Not set (night timing)"], ["MORNING", "Morning (day rolls at 11:00 PM IST)"], ["NIGHT", "Night (day rolls at 8:30 AM IST)"]];

/** Tappable multi-select chips. value is an array of codes. */
function MultiChips({ options, value, onChange }) {
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="chip-select">
      {options.map(([v, l]) => (
        <button type="button" key={v} className={"chip-opt" + (value.includes(v) ? " on" : "")} onClick={() => toggle(v)}>
          {value.includes(v) && <Icon.check width="13" height="13" />}{l}
        </button>
      ))}
    </div>
  );
}

/** Compact multi-select dropdown (checkbox list) — saves space vs. chips. */
function MultiDropdown({ options, value, onChange, placeholder }) {
  const [open, setOpen] = useState(false);
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const label = value.length === 0 ? (placeholder || "Select…")
    : value.length <= 2 ? value.join(", ") : `${value.length} selected`;
  return (
    <div className="msd">
      <button type="button" className="inp msd-btn" onClick={() => setOpen((o) => !o)}>
        <span className={"msd-lbl" + (value.length ? "" : " ph")}>{label}</span>
        <Icon.chevron width="14" height="14" />
      </button>
      {open && (
        <>
          <div className="ctx-backdrop" onClick={() => setOpen(false)} />
          <div className="msd-menu">
            {options.map(([v, l]) => (
              <label key={v} className="msd-row">
                <input type="checkbox" checked={value.includes(v)} onChange={() => toggle(v)} /> {l}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/** Shared CAS name + Employee ID + roles + clients + (conditional) processes fields. */
function AccessFields({ v, set, onRoles, onProcesses, onGroups }) {
  const [clientOpts, setClientOpts] = useState([]);
  useEffect(() => {
    api.listGroups().then((g) => setClientOpts((g || []).map((x) => [x.name, x.name]))).catch(() => setClientOpts([]));
  }, []);
  // Super admin sees every group, so no group is needed. Everyone else who is group-scoped — including an ADMIN
  // (an admin is limited to the group(s) chosen here) — picks their client group(s).
  const groupBased = !v.roles.includes("SUPER_ADMIN") && (
    v.roles.includes("USER") || v.roles.includes("SUPERVISOR") || v.roles.includes("CLIENT_ADMIN") || v.roles.includes("AUDITOR") || v.roles.includes("ADMIN"));
  const hideEmpId = v.roles.length === 1 && v.roles[0] === "CLIENT_ADMIN";   // Client admin has no Employee ID
  return (
    <>
      <div className="field"><label>User type (select one or more)</label><MultiChips options={ROLE_OPTS} value={v.roles} onChange={onRoles} /></div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div className="field"><label>First name</label><input className="inp" value={v.firstName} onChange={set("firstName")} placeholder="First" /></div>
        <div className="field"><label>Last name</label><input className="inp" value={v.lastName} onChange={set("lastName")} placeholder="Last" /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: hideEmpId ? "1fr" : "1fr 1fr", gap: 16 }}>
        <div className="field"><label>CAS name</label><input className="inp mono" value={v.casName} onChange={set("casName")} placeholder="LACN-SL" /></div>
        {!hideEmpId && <div className="field"><label>Employee ID</label><input className="inp" value={v.employeeId} onChange={set("employeeId")} placeholder="EMP-1024" /></div>}
      </div>
      {groupBased && (
        <div className="field"><label>Clients (groups this person works for)</label>
          {clientOpts.length
            ? <MultiDropdown options={clientOpts} value={v.groups || []} onChange={onGroups} placeholder="Select client groups…" />
            : <p className="muted" style={{ margin: 0 }}>No client groups yet — add them in Data management → Groups.</p>}
        </div>
      )}
      {v.roles.includes("USER") && (
        <div className="field"><label>Processes (what this user does)</label><MultiChips options={PROC_OPTS} value={v.processes} onChange={onProcesses} /></div>
      )}
      {v.roles.includes("USER") && (
        <div className="field"><label>Shift (when their allocation / completed / left counts roll over)</label>
          <select className="inp" value={v.shift || ""} onChange={set("shift")}>
            {SHIFT_OPTS.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
          </select>
        </div>
      )}
    </>
  );
}

export function UserManagement() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [wizard, setWizard] = useState(false);
  const [editing, setEditing] = useState(null);   // user being edited
  const [removing, setRemoving] = useState(null);  // user pending delete confirm

  const load = useCallback(async () => {
    setErr("");
    try { setRows(await api.listUsers()); }
    catch (e) { setErr(e.message); setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = (rows || []).filter((u) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return [u.fullName, u.email, u.casName, u.role].some((v) => (v || "").toLowerCase().includes(s));
  });

  const toggleStatus = async (u) => {
    if (u.status === "PENDING") { toast("Finish onboarding before changing status", "info"); return; }
    const next = u.status === "SUSPENDED" ? "ACTIVE" : "SUSPENDED";
    try { await api.setStatus(u.id, next); toast(`${u.fullName} ${next.toLowerCase()}`, "ok"); load(); }
    catch (e) { toast(e.message, "err"); }
  };

  return (
    <div className="um-wrap">
      <div className="toolbar">
        <div className="search"><Icon.search /><input className="inp" placeholder="Search name, email, CAS or role…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <button className="btn btn-primary" onClick={() => setWizard(true)}><Icon.plus />Add user</button>
      </div>

      <div className="card cmd-card"><div className="table-wrap">
        <table className="tbl um-table cmd-table">
          <colgroup>
            <col style={{ width: "300px" }} /><col style={{ width: "130px" }} /><col style={{ width: "120px" }} />
            <col style={{ width: "110px" }} /><col style={{ width: "150px" }} /><col style={{ width: "150px" }} />
          </colgroup>
          <thead><tr>
            <th>Team member</th><th>CAS name</th><th>Role</th>
            <th>Status</th><th>Last active</th><th style={{ textAlign: "right" }}>Manage</th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id}>
                <td><div className="um-member">
                  <div className="avatar">{initials(u.fullName)}</div>
                  <div className="um-member-txt"><div className="cell-strong">{u.fullName}</div><div className="cell-sub">{u.email}</div></div>
                </div></td>
                <td className="mono">{u.casName || "—"}</td>
                <td>{UM_ROLE[u.role] || u.role}</td>
                <td><span className={"um-status s-" + (u.status || "").toLowerCase()}>{UM_STATUS[u.status] || u.status}</span></td>
                <td className="cell-sub">{u.lastActive ? fmtDateTime(u.lastActive) : "—"}</td>
                <td>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    {u.status !== "PENDING" && (
                      <button className="btn btn-sm btn-ghost" onClick={() => toggleStatus(u)}>
                        {u.status === "ACTIVE" ? "Suspend" : "Reactivate"}
                      </button>
                    )}
                    <button className="icon-action" title="Edit user" onClick={() => setEditing(u)}><Icon.edit /></button>
                    <button className="icon-action danger" title="Delete user" onClick={() => setRemoving(u)}><Icon.trash /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows === null && <div className="empty"><Spinner /></div>}
        {err && <div className="err-text" style={{ padding: 18 }}>{err}</div>}
        {rows && filtered.length === 0 && !err && <Empty icon={<Icon.users width="40" height="40" />} title="No users match" text="Adjust your search, or add a new console user." />}
      </div></div>

      {wizard && <AddUserWizard onClose={() => setWizard(false)} onDone={() => { setWizard(false); load(); }} />}
      {editing && <EditUserModal user={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {removing && <DeleteUserModal user={removing} onClose={() => setRemoving(null)} onDone={() => { setRemoving(null); load(); }} />}
    </div>
  );
}

/* --- edit user: modify identity + access tier --- */
function EditUserModal({ user, onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nameParts = (user.fullName || "").trim().split(/\s+/);
  const [form, setForm] = useState({
    firstName: user.firstName || nameParts[0] || "",              // use the stored first/last directly
    lastName: user.lastName || nameParts.slice(1).join(" ") || "", // (don't re-split fullName — it mangles multi-word names)
    casName: user.casName || "",
    employeeId: user.employeeId || "",
    email: user.email || "",
    roles: user.roles && user.roles.length ? user.roles : [user.role || "USER"],
    processes: user.processes || [],
    groups: user.groups || [],
    shift: user.shift || "",
  });
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  // Temporary-password reset (separate from the identity save below).
  const [pwOpen, setPwOpen] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  const doResetPw = async () => {
    if (newPw.length < 8) { setErr("Temporary password must be at least 8 characters."); return; }
    setErr(""); setPwBusy(true);
    try {
      await api.resetUserPassword(user.id, newPw);
      toast(`Temporary password set for ${user.fullName}`, "ok");
      setNewPw(""); setPwOpen(false);
    } catch (e) { setErr(e.message); } finally { setPwBusy(false); }
  };

  const save = async () => {
    setErr("");
    const caOnly = form.roles.length === 1 && form.roles[0] === "CLIENT_ADMIN";
    if (!form.firstName || !form.lastName || !form.casName || (!caOnly && !form.employeeId) || !form.email) {
      setErr("All fields are required."); return;
    }
    if (form.roles.length === 0) { setErr("Select at least one user type."); return; }
    if (form.roles.includes("USER") && form.processes.length === 0) { setErr("Select at least one process for a User."); return; }
    if (form.roles.includes("ADMIN") && !form.roles.includes("SUPER_ADMIN") && (form.groups || []).length === 0) { setErr("Select the group this admin manages."); return; }
    setBusy(true);
    try {
      await api.updateUser(user.id, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        casName: form.casName.trim(),
        employeeId: form.employeeId.trim(),
        email: form.email.trim(),
        roles: form.roles,
        processes: form.roles.includes("USER") ? form.processes : [],
        groups: form.groups,
        shift: form.roles.includes("USER") ? (form.shift || null) : null,
      });
      toast(`${form.firstName} ${form.lastName} updated`, "ok");
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal wide title="Edit user" sub={user.email} onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? <span className="spin" /> : "Save changes"}</button></>}>
      <div>
        <AccessFields v={form}
          set={set}
          onRoles={(roles) => setForm((s) => ({ ...s, roles }))}
          onProcesses={(processes) => setForm((s) => ({ ...s, processes }))}
          onGroups={(groups) => setForm((s) => ({ ...s, groups }))} />
        <div className="field"><label>Work email</label><input className="inp" type="email" value={form.email} onChange={set("email")} placeholder="name@practo-priorauth.local" /></div>

        <div className="field" style={{ borderTop: "1px solid #e2e8f0", paddingTop: 14, marginTop: 4 }}>
          {!pwOpen ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setErr(""); setPwOpen(true); }}>
              Reset password…
            </button>
          ) : (
            <>
              <label>Temporary password</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <PasswordInput name="reset-password" autoComplete="new-password" placeholder="At least 8 characters"
                    value={newPw} onChange={(e) => setNewPw(e.target.value)} />
                </div>
                <button type="button" className="btn btn-primary btn-sm" onClick={doResetPw} disabled={pwBusy}>{pwBusy ? <span className="spin" /> : "Set"}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setPwOpen(false); setNewPw(""); }}>Cancel</button>
              </div>
              <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>The user signs in with this and can change it later. Takes effect immediately — you don't need to Save changes.</p>
            </>
          )}
        </div>

        {err && <div className="err-text">{err}</div>}
      </div>
    </Modal>
  );
}

/* --- delete user: confirm before removing --- */
function DeleteUserModal({ user, onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const remove = async () => {
    setErr(""); setBusy(true);
    try {
      await api.deleteUser(user.id);
      toast(`${user.fullName} deleted`, "ok");
      onDone();
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  return (
    <Modal title="Delete user" sub="This action cannot be undone" onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger" onClick={remove} disabled={busy}>{busy ? <span className="spin dark" /> : "Delete user"}</button></>}>
      <p style={{ margin: 0, fontSize: "var(--fs-base)", color: "var(--ink-2)", lineHeight: 1.6 }}>
        Are you sure you want to permanently delete <b>{user.fullName}</b> ({user.email})?
        Their console account and access will be removed immediately.
      </p>
      {err && <div className="err-text">{err}</div>}
    </Modal>
  );
}

/* --- add user: one compact form, super-admin sets the password, account goes ACTIVE --- */
function AddUserWizard({ onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ident, setIdent] = useState({ firstName: "", lastName: "", casName: "", employeeId: "", email: "", roles: ["USER"], processes: [], groups: [], shift: "" });
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const set = (k) => (e) => setIdent((s) => ({ ...s, [k]: e.target.value }));

  const create = async () => {
    setErr("");
    const caOnly = ident.roles.length === 1 && ident.roles[0] === "CLIENT_ADMIN";
    if (!ident.firstName || !ident.lastName || !ident.casName || (!caOnly && !ident.employeeId) || !ident.email) { setErr("Please fill all required fields."); return; }
    if (ident.roles.length === 0) { setErr("Select at least one user type."); return; }
    if (ident.roles.includes("USER") && ident.processes.length === 0) { setErr("Select at least one process for a User."); return; }
    if (ident.roles.includes("ADMIN") && !ident.roles.includes("SUPER_ADMIN") && (ident.groups || []).length === 0) { setErr("Select the group this admin manages."); return; }
    if (pw.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pw !== pw2) { setErr("Passwords don't match."); return; }
    setBusy(true);
    try {
      const u = await api.createUser({ ...ident, processes: ident.roles.includes("USER") ? ident.processes : [], groups: ident.groups, shift: ident.roles.includes("USER") ? (ident.shift || null) : null });
      await api.setPassword(u.id, pw);
      toast(`${u.fullName} is now active`, "ok");
      onDone();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal wide title="Add user" sub="Create the account and set its password" onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={create} disabled={busy}>{busy ? <span className="spin" /> : "Create user"}</button></>}>
      <form onSubmit={(e) => e.preventDefault()} autoComplete="on">
        <input type="text" name="username" autoComplete="username" value={ident.email || ""} readOnly hidden />
        <AccessFields v={ident}
          set={set}
          onRoles={(roles) => setIdent((s) => ({ ...s, roles }))}
          onProcesses={(processes) => setIdent((s) => ({ ...s, processes }))}
          onGroups={(groups) => setIdent((s) => ({ ...s, groups }))} />
        <div className="field"><label>Work email</label><input className="inp" type="email" value={ident.email} onChange={set("email")} placeholder="name@practo-priorauth.local" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <div className="field"><label htmlFor="wz-pw">Password</label>
            <PasswordInput id="wz-pw" name="new-password" autoComplete="new-password" placeholder="At least 8 characters" value={pw} onChange={(e) => setPw(e.target.value)} /></div>
          <div className="field"><label htmlFor="wz-pw2">Confirm password</label>
            <PasswordInput id="wz-pw2" name="new-password" autoComplete="new-password" placeholder="Re-enter password" value={pw2} onChange={(e) => setPw2(e.target.value)} /></div>
        </div>
        {err && <div className="err-text">{err}</div>}
      </form>
    </Modal>
  );
}